/**
 * 图片上传服务 - 腾讯云COS版本
 * 无文件大小限制，一次上传永久保存
 */

import { adminAuth } from '@/services/adminAuth';

const UPLOAD_URL = '/upload';
const HEALTH_URL = '/upload-health';

/**
 * 上传图片到腾讯云COS
 * 无文件大小限制，图片永久保存
 * @param file 文件对象
 * @returns Promise<string> 图片URL（COS完整URL或本地路径）
 */
export async function uploadImage(file: File): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('[上传服务] 开始上传:', file.name, '大小:', (file.size / 1024 / 1024).toFixed(2), 'MB');
    
    const token = adminAuth.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`上传失败: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    if (result.success && result.url) {
      console.log('[上传服务] 成功:', result.url, '存储类型:', result.storage || 'unknown');
      return result.url;
    }
    throw new Error('服务器返回无效');
  } catch (error) {
    console.error('[上传服务] 上传失败:', error);
    throw error;  // 不再回退到base64，直接抛出错误
  }
}

/**
 * 检查上传服务器是否可用
 */
export async function checkUploadServer(): Promise<boolean> {
  try {
    const response = await fetch(HEALTH_URL, {
      method: 'GET',
      signal: AbortSignal.timeout(3000),
    });
    if (response.ok) {
      const data = await response.json();
      console.log('[上传服务] 健康检查:', data);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * 验证文件类型
 * @param file 文件对象
 * @returns boolean 是否允许
 */
export function isValidImageType(file: File): boolean {
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
  ];
  return allowedTypes.includes(file.type);
}

/**
 * 获取文件扩展名
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || 'jpg';
}
